import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsAuthenticated } from "../authentication/authenticationSlice";
import {
  selectConsentStatus,
  selectConsentLoadingStatus,
} from "./instructor-consent-slice";
import { fetchConsentStatus } from "./slice-extensions/instructor-consent-slice-extension";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ROUTES } from "../../routes";

interface DocumentRowProps {
  name: string;
  version: string;
  acceptedAt: string | null;
  to: string;
}

/**
 * Settings card listing the legal documents the instructor has accepted, with the
 * version and the date each was accepted — the durable record of consent surfaced
 * back to the person who gave it.
 */
const LegalConsentSettings = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const status = useAppSelector(selectConsentStatus);
  const loadingStatus = useAppSelector(selectConsentLoadingStatus);

  useEffect(() => {
    if (isAuthenticated && !status && loadingStatus === LoadingStatus.Idle) {
      dispatch(fetchConsentStatus());
    }
  }, [isAuthenticated, status, loadingStatus, dispatch]);

  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const DocumentRow = ({ name, version, acceptedAt, to }: DocumentRowProps) => (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="font-medium text-grey-20 dark:text-white">{name}</p>
        <p className="text-sm text-grey-50 dark:text-grey-70">
          {acceptedAt
            ? t("settings.legal.acceptedOn", {
                version,
                date: formatDate(acceptedAt),
              })
            : t("settings.legal.notAccepted")}
        </p>
      </div>
      <Link
        to={to}
        className="shrink-0 text-sm text-primary-40 dark:text-primary-90 hover:underline"
      >
        {t("settings.legal.view")}
      </Link>
    </div>
  );

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6 transition-colors hover:border-primary-50/40 dark:hover:border-primary-70/40">
      <div className="mb-2 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <ShieldCheck size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">
            {t("settings.legal.title")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {t("settings.legal.description")}
          </p>
        </div>
      </div>

      {status && (
        <div className="divide-y divide-grey-90 dark:divide-grey-30">
          <DocumentRow
            name={t("auth.termsOfService", "Terms of Service")}
            version={status.termsVersion}
            acceptedAt={status.termsAccepted ? status.termsAcceptedAt : null}
            to={ROUTES.TERMS_OF_SERVICE}
          />
          <DocumentRow
            name={t("auth.privacyPolicy", "Privacy Policy")}
            version={status.privacyVersion}
            acceptedAt={status.privacyAccepted ? status.privacyAcceptedAt : null}
            to={ROUTES.PRIVACY_POLICY}
          />
        </div>
      )}
    </div>
  );
};

export default LegalConsentSettings;
